import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import { authSelectors } from "../../../ducks/auth";
import { Group, groupActions, groupSelectors } from "../../../ducks/group";
import { UserRole } from "../../../ducks/user";
import { useNavigation } from "../../../hooks/useNavigation";
import { TableHeaderItem, TableSortBy } from "../../../hooks/useTable";
import { AppDispatch } from "../../../store";
import { Link } from "../../atoms/Link";

export const useGroups = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();

  const groups = useSelector(groupSelectors.groups);
  const loggedInUser = useSelector(authSelectors.loggedInUser);

  const canCreate = useMemo(() => {
    if (!loggedInUser) return false;

    return loggedInUser.role === UserRole.Admin;
  }, [loggedInUser]);

  const canEdit = useMemo(() => {
    if (!loggedInUser) return false;

    return loggedInUser.role === UserRole.Admin;
  }, [loggedInUser]);

  const fetchGroups = useCallback((sort: TableSortBy) => {
    dispatch(appActions.showLoading());

    dispatch(
      groupActions.fetchGroups.started({
        // @ts-expect-error
        sort_key: sort.field,
        sort_order: sort.order,
        onComplete: () => dispatch(appActions.hideLoading()),
      }),
    );
  }, []);

  const tableHeaders: TableHeaderItem<Group>[] = useMemo(() => {
    return [
      {
        id: 1,
        title: "顧客名",
        popupTitle: "顧客名",
        dataKey: "clientName",
        sortKey: "customers.name",
      },
      {
        id: 2,
        title: "グループ名",
        popupTitle: "グループ名",
        dataKey: "name",
        sortKey: "name",
        dataMapFunction: ({ id, name }) => ({
          text: name,
          href: PageRoutes.GROUP_DETAIL.replace(":groupId", `${id}`),
        }),
        customComponent: Link,
      },
    ];
  }, []);

  const handleRegistration = useCallback(() => {
    if (!canCreate) return;

    navigateToPage(PageRoutes.GROUP_REGISTRATION);
  }, [canCreate]);

  const handleEdit = useCallback(
    (data: Group) => {
      if (!canEdit) return;

      navigateToPage(PageRoutes.GROUP_EDIT.replace(":groupId", `${data.id}`));
    },
    [canEdit],
  );

  return {
    canCreate,
    canEdit,
    groups,
    tableHeaders,
    fetchGroups,
    handleRegistration,
    handleEdit,
  };
};
